import { handleApiResponse } from '@src/api/apiClient/axiosClient';
import { firstResetPasswordApiClient } from '@src/api/tenant/auth/firstResetPasswordApiClient';
import { tenantSiteApiClient } from '@src/api/tenant/site/tenantSiteApiClient.ts';
import { PasswordRuleDescription } from '@src/components/PasswordRuleDescription';
import { fetchTenantUserProfile } from '@src/context/auth/ProfileFetcher';
import { useTenantAuth } from '@src/context/auth/useTenantAuth';
import { useToast } from '@src/features/toast/context/toastContext';
import TOAST_MESSAGE from '@src/modules/constants/toastMessage';
import { TENANT_URL } from '@src/routes/tenant';
import { firstError } from '@src/utils/errorMessageUtils';
import { useState } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { Link, useLocation, useNavigate } from 'react-router-dom';

import { TogglePassword } from './components/TogglePassword';

function SetNewPasswordForm() {
  type requestParam = {
    email: string;
    password: string;
    new_password: string;
    new_password_confirmation: string;
  };

  const location = useLocation();
  const navigate = useNavigate();
  const toast = useToast();
  const tenantAuth = useTenantAuth();

  const [locationData] = useState<{ email: string }>(location.state as { email: string });

  const {
    register,
    handleSubmit,
    formState: { errors },
    setError,
    clearErrors,
  } = useForm<requestParam>();

  const onSubmit: SubmitHandler<requestParam> = async (data) => {
    clearErrors();
    const res = await firstResetPasswordApiClient.resetPassword({
      email: locationData.email,
      password: data.password,
      new_password: data.new_password,
      new_password_confirmation: data.new_password_confirmation,
    });
    const password = data.new_password;
    handleApiResponse(
      res,
      async (data) => {
        if (data.two_factor_enabled) {
          toast(TOAST_MESSAGE.SEND);
          navigate(TENANT_URL.TWO_FACTOR, {
            state: {
              email: locationData.email,
              password: password,
              is_first_login: data.is_first_login,
              session: data.session,
            },
          });
          return;
        }
        fetchTenantUserProfile(tenantAuth.setProfile);
        toast(TOAST_MESSAGE.REGISTER);
        const res = await tenantSiteApiClient.topPageUrl();
        handleApiResponse(res, (data) => {
          navigate(data.url);
        });
      },
      (errors) => {
        setError('password', {
          type: 'manual',
          message: firstError(errors.password || errors.message),
        });
        setError('new_password', {
          type: 'manual',
          message: firstError(errors.new_password),
        });
        setError('new_password_confirmation', {
          type: 'manual',
          message: firstError(errors.new_password_confirmation),
        });
      },
    );
  };

  const [isRevealCurrentPassword, setIsRevealCurrentPassword] = useState(false);
  const [isRevealNewPassword, setIsRevealNewPassword] = useState(false);
  const [isRevealNewPasswordConfirmation, setIsRevealNewPasswordConfirmation] = useState(false);

  const toggleCurrentPassword = () => {
    setIsRevealCurrentPassword((prevState) => !prevState);
  };

  const toggleNewPassword = () => {
    setIsRevealNewPassword((prevState) => !prevState);
  };

  const toggleNewPasswordConfirmation = () => {
    setIsRevealNewPasswordConfirmation((prevState) => !prevState);
  };

  return (
    <div className='flex flex-col gap-8'>
      <h1 className='text-center text-base font-medium'>新しいパスワードを設定してください</h1>
      <form
        className='flex flex-col gap-6'
        onSubmit={handleSubmit(onSubmit)}
      >
        <p>{locationData.email}</p>
        <div className='flex flex-col gap-[26px]'>
          <div className='flex flex-col gap-4'>
            <div className='flex flex-col gap-[6px]'>
              <label
                className='label-login'
                htmlFor='password'
              >
                現在のパスワード
              </label>
              <div className='relative'>
                <input
                  id='password'
                  type={isRevealCurrentPassword ? 'text' : 'password'}
                  className={`input-login ${errors.password ? `error-input` : ``}`}
                  placeholder='現在のパスワード'
                  {...register('password')}
                />
                <button
                  type='button'
                  onClick={toggleCurrentPassword}
                  className='toggle-password'
                >
                  <TogglePassword isReveal={isRevealCurrentPassword} />
                </button>
              </div>
              {errors.password && <p className='error-message'>{errors.password.message}</p>}
            </div>
            <div className='flex flex-col gap-[6px]'>
              <label
                className='label-login'
                htmlFor='new_password'
              >
                新しいパスワード
              </label>
              <div className='relative'>
                <input
                  id='new_password'
                  type={isRevealNewPassword ? 'text' : 'password'}
                  className={`input-login ${errors.new_password ? `error-input` : ``}`}
                  placeholder='新しいパスワード'
                  {...register('new_password')}
                />
                <button
                  type='button'
                  onClick={toggleNewPassword}
                  className='toggle-password'
                >
                  <TogglePassword isReveal={isRevealNewPassword} />
                </button>
              </div>
              {errors.new_password && (
                <p className='error-message'>{errors.new_password.message}</p>
              )}
              <PasswordRuleDescription />
            </div>
            <div className='flex flex-col gap-[6px]'>
              <label
                className='text-base font-medium'
                htmlFor='new_password_confirmation'
              >
                新しいパスワード(確認用)
              </label>
              <div className='relative'>
                <input
                  id='new_password_confirmation'
                  type={isRevealNewPasswordConfirmation ? 'text' : 'password'}
                  className={`input-login ${errors.new_password_confirmation ? `error-input` : ``}`}
                  placeholder='新しいパスワード(確認用)'
                  {...register('new_password_confirmation')}
                />
                <button
                  type='button'
                  onClick={toggleNewPasswordConfirmation}
                  className='toggle-password'
                >
                  <TogglePassword isReveal={isRevealNewPasswordConfirmation} />
                </button>
              </div>
              {errors.new_password_confirmation && (
                <p className='error-message'>{errors.new_password_confirmation.message}</p>
              )}
            </div>
          </div>
          <div className='flex justify-center'>
            <Link
              className='text-sm text-primary-800 underline hover:no-underline'
              to={TENANT_URL.PASSWORD_RESET_SENDMAIL}
            >
              パスワードを忘れた場合
            </Link>
          </div>
          <button
            className='btn-login-tenant'
            type='submit'
          >
            パスワードを更新
          </button>
        </div>
      </form>
    </div>
  );
}

export default SetNewPasswordForm;
